// @ts-ignore - @rails/actioncable does not have type definitions
import { createConsumer, Cable, Channel } from '@rails/actioncable'
import { store } from './store'
import { env } from '../../lib/env.js'
import { resolveCableUrl } from './websocket-url.js'

export interface CampaignUpdate {
  campaign_id: string | number
  status: 'not_sent' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'failed'
  sent_count: number
  delivered_count: number
  recipients_count: number
  updated_at: string
}

export interface ImportProgress {
  status: 'processing' | 'completed' | 'failed' | 'queued'
  message: string
  progress: number
  total: number
  imported: number
  contacts_added?: number
  processed?: number
  errors?: string[]
  warnings?: string[]
  success_rate?: number
  timestamp?: string
}

export interface WebSocketMessage {
  type: 'campaign_status_update' | 'import_progress'
  data: CampaignUpdate | ImportProgress
}

class WebSocketService {
  private consumer: Cable | null = null
  private tenantChannel: Channel | null = null
  private importProgressChannel: Channel | null = null
  private isConnected = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000 // Start with 1 second
  private callbacks: Map<string, Set<(data: any) => void>> = new Map()

  connect(authToken: string): Promise<void> {
    // Don't connect if already connected
    if (this.isConnected && this.consumer) {
      return Promise.resolve()
    }

    // Disconnect existing connection first
    if (this.consumer) {
      this.disconnect()
    }

    return new Promise((resolve, reject) => {
      try {
        // Build the cable URL. In a browser the SPA is same-origin with
        // /cable (vite preview proxies the ws upgrade to crm-api). In the
        // desktop (Tauri) app window.location.origin is tauri://localhost
        // (macOS/Linux) / http://tauri.localhost (Windows) — no /cable server
        // there — so target the real dashboard host (env.apiUrl) instead.
        // Full rationale + edge cases in websocket-url.ts (pinned by its tests).
        const wsUrl = resolveCableUrl(
          {
            origin: window.location.origin,
            protocol: window.location.protocol,
            hostname: window.location.hostname,
          },
          env.apiUrl,
        )


        // SECURITY NOTE: Auth token is currently passed via URL query parameter
        // because Rails ActionCable's default Connection identifier reads it
        // from request.params. URL query strings can leak through proxy logs,
        // referer headers, and browser history. This is acceptable here only
        // because the URL is wss:// in production (TLS-encrypted) and the
        // server logs the cable connection without query strings.
        //
        // TODO(security): migrate ActionCable to use a cookie or
        // Sec-WebSocket-Protocol subprotocol-based auth. Tracked separately
        // from this audit; this needs a server-side Connection change.
        this.consumer = createConsumer(`${wsUrl}?token=${authToken}`)
        
        // Subscribe to tenant channel
        this.tenantChannel = this.consumer.subscriptions.create('TenantChannel', {
          connected: () => {
            // Also subscribe to import progress channel
            this.subscribeToImportProgress()
            this.isConnected = true
            this.reconnectAttempts = 0
            this.reconnectDelay = 1000
            resolve()
          },
          
          disconnected: () => {
            this.isConnected = false
            this.attemptReconnect(authToken)
          },
          
          received: (data: any) => {
            // ActionCable sends data with message wrapper, extract the actual message
            if (data && data.message) {
              this.handleMessage(data.message)
            } else {
              this.handleMessage(data)
            }
          },
          
          rejected: () => {
            console.error('WebSocket: ❌ Connection rejected - check authentication')
            this.isConnected = false
            reject(new Error('WebSocket connection rejected - authentication failed'))
          }
        })
        
        // Set timeout for connection
        setTimeout(() => {
          if (!this.isConnected) {
            console.error('WebSocket: ⏰ Connection timeout after 10 seconds')
            reject(new Error('WebSocket connection timeout'))
          }
        }, 10000) // 10 second timeout
        
      } catch (error) {
        console.error('WebSocket: ❌ Connection error', error)
        reject(error)
      }
    })
  }

  disconnect(): void {
    
    if (this.tenantChannel) {
      this.tenantChannel.unsubscribe()
      this.tenantChannel = null
    }
    
    if (this.importProgressChannel) {
      this.importProgressChannel.unsubscribe()
      this.importProgressChannel = null
    }
    
    if (this.consumer) {
      this.consumer.disconnect()
      this.consumer = null
    }
    
    this.isConnected = false
    this.callbacks.clear()
  }

  private subscribeToImportProgress(): void {
    if (!this.consumer) {
      console.warn('WebSocket: Cannot subscribe to import progress - no consumer')
      return
    }

    // Subscribe to user-specific import progress channel
    this.importProgressChannel = this.consumer.subscriptions.create('ImportProgressChannel', {
      connected: () => {
      },
      
      disconnected: () => {
      },
      
      received: (data: any) => {
        // Format the message as a WebSocketMessage
        const message: WebSocketMessage = {
          type: 'import_progress',
          data: data as ImportProgress
        }
        this.handleMessage(message)
      },
      
      rejected: () => {
        console.error('WebSocket: ❌ ImportProgressChannel subscription rejected')
      }
    })
  }

  private async attemptReconnect(authToken: string): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('WebSocket: Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)
    
    
    setTimeout(async () => {
      try {
        await this.connect(authToken)
      } catch (error) {
        console.error('WebSocket: Reconnection failed', error)
      }
    }, delay)
  }

  private handleMessage(message: WebSocketMessage): void {
    
    if (!message || typeof message !== 'object') {
      console.warn('WebSocket: Invalid message format:', message)
      return
    }

    const { type, data } = message
    
    if (!type) {
      console.warn('WebSocket: Message missing type field:', message)
      return
    }
    
    
    // Trigger callbacks for this message type
    const typeCallbacks = this.callbacks.get(type)
    if (typeCallbacks && typeCallbacks.size > 0) {
      typeCallbacks.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('WebSocket: Error in callback for type', type, ':', error)
        }
      })
    } else {
    }
    
    // Trigger callbacks for 'all' events
    const allCallbacks = this.callbacks.get('all')
    if (allCallbacks && allCallbacks.size > 0) {
      allCallbacks.forEach(callback => {
        try {
          callback(message)
        } catch (error) {
          console.error('WebSocket: Error in all-events callback:', error)
        }
      })
    }
  }

  // Subscribe to specific message types
  on(eventType: string, callback: (data: any) => void): () => void {
    if (!this.callbacks.has(eventType)) {
      this.callbacks.set(eventType, new Set())
    }
    
    this.callbacks.get(eventType)!.add(callback)
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.callbacks.get(eventType)
      if (callbacks) {
        callbacks.delete(callback)
        if (callbacks.size === 0) {
          this.callbacks.delete(eventType)
        }
      }
    }
  }

  // Request immediate update of campaigns data
  requestCampaignsUpdate(): void {
    if (this.tenantChannel && this.isConnected) {
      this.tenantChannel.perform('request_campaigns_update')
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected
  }
}

// Singleton instance
export const webSocketService = new WebSocketService()
export default webSocketService
